/**
 * Legal identity of the publisher and its hosting provider.
 *
 * Values kept between square brackets are STILL TO BE FILLED IN: they cannot be guessed, and the
 * law (LCEN, art. 6-III) requires them to be exact. They are rendered as-is on the page — a
 * visible bracket in production is better than an invented figure that looks real.
 *
 * The registry data below (name, legal form, SIREN, SIRET, VAT, registered office) comes from the
 * French official company registry (API Recherche d'entreprises, SIREN 989364146) via the AFFIGES
 * project, checked on 2026-08-28. Re-check it before publishing if the company details changed.
 */
export const LEGAL_ENTITY = {
  // ── Publisher ────────────────────────────────────────────────────────────
  service: 'Espace Client',
  siteUrl: 'www.espaceclient.io',
  societe: 'ISEFFI',
  formeJuridique: 'société par actions simplifiée (SAS)',
  capital: '10 000 €',
  rcsVille: 'Saint-Brieuc',
  siren: '989 364 146',
  siret: '989 364 146 00013',
  tva: 'FR23989364146',
  adresse: '4 rue Louis de Broglie, 22300 Lannion',
  // A named individual, as the LCEN expects — not "the legal representative", all the more so
  // since ISEFFI's president is itself a legal entity in the registry.
  directeur: 'Michel L’Hostis',
  email: 'contact@iseffi.com',
  telephone: '02 98 78 39 40',

  // ── Hosting provider ─────────────────────────────────────────────────────
  // The LCEN requires the name, address and phone number of the host. The production server is
  // reached over SSH at /home/innovtel/… (see apache-prod-conf/), which does not name a provider:
  // this must be filled in by the publisher rather than assumed.
  hebergeur: '[hébergeur — raison sociale à compléter]',
  hebergeurAdresse: '[adresse postale de l’hébergeur à compléter]',
  hebergeurTelephone: '[téléphone de l’hébergeur à compléter]',
  hebergeurLocalisation: '[pays d’hébergement des serveurs à compléter]',

  // ── Data protection ──────────────────────────────────────────────────────
  // Appointing a DPO is not mandatory for a company of this size; the contact address is what
  // matters for exercising rights.
  dpo: '[délégué à la protection des données — à compléter si un DPO est désigné]',

  // ── Sub-processors actually wired in the codebase ─────────────────────────
  // Named because they are really configured, and no further. Wording stays conditional for the
  // ones that depend on a per-ERP setting, so it holds true whether or not the key is filled in.
  sousTraitantEmail: 'Brevo (Brevo SAS, France)',
  sousTraitantPaiement: 'Stripe (Stripe Payments Europe Ltd., Irlande)',
  sousTraitantSignature: 'Firma, Yousign ou Signaturit, selon le prestataire configuré',
  sousTraitantIA: 'Anthropic (Claude) et Vapi',
  sousTraitantFacturation: 'la plateforme de dématérialisation choisie (SuperPDP, Dext)',
} as const

/**
 * Bracketed placeholders also appear directly in the body of the terms of sale (commitment period,
 * notice period, service levels…), where no constant can detect them. Set this to `false` once
 * they have all been filled in, so the warning banner disappears.
 */
export const LEGAL_TEXT_PLACEHOLDERS = true

/** Is a bracketed value left? The warning banner on the pages depends on it. */
export const LEGAL_INCOMPLETE =
  LEGAL_TEXT_PLACEHOLDERS || Object.values(LEGAL_ENTITY).some((v) => v.includes('['))

/**
 * Last review date of the legal documents. Deliberately a constant and not `new Date()`: a page
 * that claims to have been updated today every day tells the reader nothing.
 */
export const LEGAL_UPDATED_AT = '31 août 2026'

/**
 * The four mandatory legal documents, in the order they are listed in every footer.
 *
 * The documents themselves are French-only, but their names are translated: an English-speaking
 * visitor must still be able to recognise the privacy policy in a footer.
 */
export const LEGAL_PAGES = [
  { path: '/mentions-legales', labelKey: 'legal.nav.mentions' },
  { path: '/confidentialite', labelKey: 'legal.nav.privacy' },
  { path: '/cgu', labelKey: 'legal.nav.cgu' },
  { path: '/cgv', labelKey: 'legal.nav.cgv' },
] as const
